'use client';

import { useState, useEffect } from 'react';

const COOKIE_CONSENT_KEY = 'chronomind_cookie_consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash immediately
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setVisible(false);
    // Enable GA tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'denied',
      });
    }
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setVisible(false);
    // Disable GA tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4"
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="false"
    >
      <div className="mx-auto max-w-4xl rounded-xl border border-[#C9A961]/30 bg-[#0F172A]/95 p-5 shadow-2xl backdrop-blur-md">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🍪</span>
              <h3 className="font-heading text-base font-bold text-white">Cookie Preferences</h3>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              We use cookies to improve your experience and analyze platform usage. Essential cookies are always active.
              {' '}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-[#C9A961] hover:text-[#e8c97a] underline text-sm"
              >
                {showDetails ? 'Hide details' : 'Learn more'}
              </button>
            </p>

            {showDetails && (
              <div className="mt-3 space-y-2 rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-white">Essential Cookies</p>
                    <p className="text-xs text-gray-500">Required for authentication and security</p>
                  </div>
                  <span className="text-xs text-green-400 font-medium">Always Active</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-white">Analytics Cookies</p>
                    <p className="text-xs text-gray-500">Google Analytics — helps us improve the platform</p>
                  </div>
                  <span className="text-xs text-gray-400">Optional</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-shrink-0 items-center gap-3">
            <button
              onClick={handleDecline}
              className="rounded-lg border border-white/20 bg-white/5 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="rounded-lg bg-[#C9A961] px-5 py-2 text-sm font-bold text-[#0F172A] transition-colors hover:bg-[#e8c97a]"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
